<template>
    <div class="index-scroll-wrapper">
        <Container width="landing" class="index-scroll-wrapper__container">
            <IndexYourInstrument class="index-scroll-wrapper__item _instrument" style="z-index: 1" />
            <transition name="fade">
                <IndexScrollAnyPlace v-show="showSection"
                                     class="index-scroll-wrapper__item index-scroll-wrapper__section _any-place"
                                     style="z-index: 2"
                />
            </transition>
            <transition name="fade">
                <IndexScrollComfort v-show="showSection"
                                    class="index-scroll-wrapper__item index-scroll-wrapper__section _comfort"
                                    style="z-index: 3"
                />
            </transition>
            <transition name="fade">
                <IndexScrollSaveTime v-show="showSection"
                                     class="index-scroll-wrapper__item index-scroll-wrapper__section _save-time"
                                     style="z-index: 4"
                />
            </transition>
        </Container>
    </div>
</template>

<script setup lang="ts">
    import gsap from 'gsap'
    import ScrollTrigger from 'gsap/ScrollTrigger'
    import Container from '~/src/shared/components/common/Container/Container.vue'
    import IndexScrollAnyPlace from '~/src/shared/components/pages/index/IndexScroll/IndexScrollAnyPlace.vue'
    import IndexScrollComfort from '~/src/shared/components/pages/index/IndexScroll/IndexScrollComfort.vue'
    import IndexScrollSaveTime from '~/src/shared/components/pages/index/IndexScroll/IndexScrollSaveTime.vue'
    import IndexYourInstrument from '~/src/shared/components/pages/index/IndexYourInstrument/IndexYourInstrument.vue'
    import { useWindowStore } from '~/src/shared/pinia/useWindowStore'

    const windowStore = useWindowStore()
    const isMobile = computed(() => windowStore.isMobile)

    const isAnimationInited = ref(isMobile.value)

    onMounted(async () => {
        if (!isMobile.value) {
            gsap.registerPlugin(ScrollTrigger)
            animationInit()
        }
    })

    const showSection = computed(() => isAnimationInited.value)

    let ctx: gsap.Context

    function animationInit() {
        isAnimationInited.value = true

        nextTick(() => {
            ctx = gsap.context(() => {
                const pinnedSection = document.querySelector('.index-scroll-wrapper') as HTMLDivElement
                const instrumentsRef = document.querySelector('._instrument') as HTMLDivElement
                const anyPlaceRef = document.querySelector('._any-place') as HTMLDivElement
                const comfortRef = document.querySelector('._comfort') as HTMLDivElement
                const saveTimeRef = document.querySelector('._save-time') as HTMLDivElement
                const cards = gsap.utils.toArray([anyPlaceRef, comfortRef, saveTimeRef]) as HTMLDivElement[]

                const cardCount = cards.length
                const pinnedHeight = window.innerHeight * (cardCount + 1)
                const startPositionOfCards = pinnedSection?.offsetHeight

                // Устанавливаем начальные позиции карт
                cards.forEach((card, index) => {
                    if (index === 0) {
                        gsap.set(card, {
                            transform: `translateY(${instrumentsRef?.offsetHeight - 60}px)`,
                        })
                    } else {
                        gsap.set(card, {
                            transform: `translateY(${startPositionOfCards}px)`,
                        })
                    }
                })

                // Создаем ScrollTrigger
                const scrollTrigger = ScrollTrigger.create({
                    trigger: pinnedSection,
                    start: 'top top',
                    end: `+=${pinnedHeight}`,
                    pin: true,
                    pinSpacing: true,
                    scrub: true,
                    onEnter: (self) => {
                        const progress = self.progress * cardCount
                        if (progress >= 1) {
                            gsap.set(instrumentsRef, {
                                opacity: 0,
                            })
                        }
                    },
                    onUpdate: (self) => {
                        const progress = self.progress * cardCount
                        const currentCard = Math.floor(progress)

                        cards.forEach((card, index) => {
                            if (index < currentCard) {
                                const pinnedRect = pinnedSection.getBoundingClientRect()
                                const pinnedCenterY = pinnedRect.top + pinnedRect.height / 2
                                const cardRect = card.getBoundingClientRect()
                                const cardCenterY = pinnedCenterY - cardRect.height / 2

                                gsap.set(card, {
                                    transform: `translateY(${cardCenterY}px)`,
                                })
                            } else if (index === currentCard && index === 0) {
                                const pinnedRect = pinnedSection.getBoundingClientRect()
                                const pinnedCenterY = pinnedRect.top + pinnedRect.height / 2
                                const cardProgress = progress - currentCard

                                const cardRect = card.getBoundingClientRect()
                                const cardCenterY = pinnedCenterY - cardRect.height / 2

                                const startPosition = instrumentsRef.offsetHeight - 60

                                const newTop = gsap.utils.interpolate(startPosition, cardCenterY, cardProgress)
                                const newOpacity = gsap.utils.interpolate(1, 0, cardProgress)

                                gsap.set(instrumentsRef, {
                                    opacity: newOpacity,
                                })

                                gsap.set(card, {
                                    transform: `translateY(${newTop}px)`,
                                })
                            } else if (index === currentCard && index > 0) {
                                const cardProgress = progress - currentCard
                                const pinnedRect = pinnedSection.getBoundingClientRect()
                                const pinnedCenterY = pinnedRect.top + pinnedRect.height / 2

                                const cardRect = card.getBoundingClientRect()
                                const cardCenterY = pinnedCenterY - cardRect.height / 2

                                const newTop = gsap.utils.interpolate(startPositionOfCards, cardCenterY, cardProgress)

                                gsap.set(card, {
                                    transform: `translateY(${newTop}px)`,
                                })

                                gsap.set(instrumentsRef, {
                                    opacity: 0,
                                })
                            }
                        })
                    },
                    onRefresh: () => {
                        // Обновляем размеры и позиции
                        cards.forEach((card, index) => {
                            if (index === 0) {
                                gsap.set(card, {
                                    transform: `translateY(${instrumentsRef?.offsetHeight - 60}px)`,
                                })
                            } else {
                                gsap.set(card, {
                                    transform: `translateY(${startPositionOfCards}px)`,
                                })
                            }
                        })
                    },
                })

                // Принудительное обновление ScrollTrigger после инициализации
                ScrollTrigger.refresh()

                // Проверяем позицию скролла при загрузке
                const currentScroll = window.scrollY
                const triggerOffset = pinnedSection.getBoundingClientRect().top + currentScroll

                if (currentScroll > triggerOffset) {
                    const progress = (currentScroll - triggerOffset) / pinnedHeight
                    scrollTrigger.scroll(progress * pinnedHeight > 1 ? 1 : progress * pinnedHeight)
                }
            })
        })
    }

    onUnmounted(() => {
        if (!isMobile.value) {
            ctx.kill()
            isAnimationInited.value = false
        }
    })
</script>

<style scoped lang="scss">
    .index-scroll-wrapper {
        height: 100vh;

        @include respond-to(mobile) {
            height: auto;
        }

        &__container {
            position: relative;
            height: 100%;
        }

        &__section {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            transform: translateY(150%);

            @include respond-to(mobile) {
                position: relative;
                top: initial;
                left: initial;
                margin-top: -50px;
                transform: initial;
            }
        }

        &__item {
            --container-paddings: 80px;

            @include respond-to(tablet) {
                --container-paddings: 32px;
            }

            @include respond-to(mobile) {
                --container-paddings: 12px;
            }

            &._instrument {
                margin-right: calc(-1 * var(--container-paddings));
                margin-left: calc(-1 * var(--container-paddings));
            }
        }
    }
</style>
